import { forwardRef, useCallback, useId, useMemo } from "react";
import { useAtomValue, type Atom, type PrimitiveAtom } from "jotai";
import { atomWithDefault, useAtomCallback } from "jotai/utils";
import type { PlainMessage } from "@bufbuild/protobuf";
import type { Subject } from "gitpod-next-api/gitpod/v1/identity_pb";
import { InputField } from "@/components/podkit/forms/InputField";
import { Text } from "@/components/podkit/typography/Text";
import { Button } from "@/components/flexkit/Button";
import type { PlainWorkflow } from "@/queries/automation-queries";
import { WorkflowEditAction } from "@/components/automations/AutomationEditAction";
import { WorkflowEditTriggers } from "@/components/automations/AutomationEditTriggers";
import { WorkflowEditExecutor } from "@/components/automations/AutomationEditExecutor";
import { type WorkflowEditFormTriggerData } from "@/components/automations/automation-edit-form-data";
import { cn } from "@/components/podkit/lib/cn";

export type AutomationTriggerEditData = {
    executor: PlainMessage<Subject> | undefined;
    triggers: WorkflowEditFormTriggerData[];
    maxParallel: number | undefined;
    maxTotal: number | undefined;
};

type AutomationTriggerEditAreaProps = {
    workflow: PlainWorkflow;
    executorAtom: PrimitiveAtom<PlainMessage<Subject> | undefined>;
    triggersAtom: PrimitiveAtom<WorkflowEditFormTriggerData[]>;
    maxParallelAtom: PrimitiveAtom<number | undefined>;
    maxTotalAtom: PrimitiveAtom<number | undefined>;
    onUpdate: (data: AutomationTriggerEditData) => void;
    onCancel: () => void;
    className?: string;
};

// Creates a local writable atom that derives initial value from parent atom
const useAtomWithDefault = <T,>(parentAtom: Atom<T>): PrimitiveAtom<T> => {
    return useMemo(() => atomWithDefault<T>((get) => get(parentAtom)), [parentAtom]);
};

export const AutomationTriggerEditArea = forwardRef<HTMLDivElement, AutomationTriggerEditAreaProps>(
    (
        {
            workflow,
            executorAtom: parentExecutorAtom,
            triggersAtom: parentTriggersAtom,
            maxParallelAtom: parentMaxParallelAtom,
            maxTotalAtom: parentMaxTotalAtom,
            onUpdate,
            onCancel,
            className,
        },
        ref,
    ) => {
        const executorId = useId();

        // Create local atoms that derive initial values from parent atoms
        const executorAtom = useAtomWithDefault(parentExecutorAtom);
        const triggersAtom = useAtomWithDefault(parentTriggersAtom);
        const maxParallelAtom = useAtomWithDefault(parentMaxParallelAtom);
        const maxTotalAtom = useAtomWithDefault(parentMaxTotalAtom);

        // Read executor for the executor selector
        const executor = useAtomValue(executorAtom);

        const handleExecutorChange = useAtomCallback(
            useCallback(
                (_get, set, newExecutor: PlainMessage<Subject> | undefined) => {
                    set(executorAtom, newExecutor);
                },
                [executorAtom],
            ),
        );

        const handleUpdate = useAtomCallback(
            useCallback(
                (get) => {
                    onUpdate({
                        executor: get(executorAtom),
                        triggers: get(triggersAtom),
                        maxParallel: get(maxParallelAtom),
                        maxTotal: get(maxTotalAtom),
                    });
                },
                [executorAtom, triggersAtom, maxParallelAtom, maxTotalAtom, onUpdate],
            ),
        );

        return (
            <div
                ref={ref}
                className={cn(
                    "relative flex flex-1 grow flex-col overflow-auto border-l border-border-subtle",
                    className,
                )}
            >
                <Text className="p-6 text-lg font-medium">Edit trigger</Text>
                <div className="flex flex-1 flex-col gap-6 bg-surface-primary px-6">
                    <InputField
                        label="Run as"
                        hint="Select who runs this automation, you or a service account"
                        id={executorId}
                    >
                        <WorkflowEditExecutor
                            selectedExecutorId={executor?.id}
                            onExecutorChange={handleExecutorChange}
                            className="w-full"
                        />
                    </InputField>
                    <WorkflowEditTriggers workflow={workflow} triggersAtom={triggersAtom} />
                    <WorkflowEditAction maxParallelAtom={maxParallelAtom} maxTotalAtom={maxTotalAtom} />
                </div>
                <div className="flex justify-end gap-2 border-t border-border-subtle py-4 pl-4 pr-6">
                    <Button variant="outline" onClick={onCancel} data-tracking-id="cancel">
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={handleUpdate} data-tracking-id="update">
                        Update
                    </Button>
                </div>
            </div>
        );
    },
);

AutomationTriggerEditArea.displayName = "AutomationTriggerEditArea";
